import React, { useState } from 'react';
import { Award, CheckCircle, GraduationCap } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const LandmarkEducation = () => {
  const [activeCard, setActiveCard] = useState(null);

  const courses = [
    { 
      name: 'The Advanced Course', 
      date: '01-Oct-93',
      impact: "Transformation isn't about fixing life—it's about generating it."
    },
    { 
      name: 'The Landmark Forum', 
      date: '15-Oct-93',
      impact: "Discovery that 'who I am is not my history, but the space in which my future is invented.'"
    },
    { 
      name: 'Self-Expression and Leadership Program (SELP)', 
      date: '01-Dec-93',
      impact: "Leadership as authentic self-expression; taking a stand for community projects."
    },
    { 
      name: 'Communication: Access to Power', 
      date: '04-Mar-95',
      impact: "Language as the architecture of reality; rewired how I listen."
    },
    { 
      name: 'Communication: Performance and Power', 
      date: '22-Jul-95',
      impact: "Speaking as a creative act; generating results through committed speaking."
    },
    { 
      name: 'Inventing Oneself', 
      date: '01-Sep-95',
      impact: "Creating myself as the source of my own possibilities and power."
    },
    { 
      name: 'Sex and Intimacy', 
      date: '11-Jul-96',
      impact: "Authentic connection and vulnerability as the foundation of intimacy."
    },
    { 
      name: 'Introduction Leaders Program (ILP)', 
      date: '1996',
      impact: "Mastered the art of inviting others into possibility through enrollment."
    },
    { 
      name: 'The Wisdom Course', 
      date: '1999',
      impact: "Life isn't something to manage; it's something to dance with."
    },
    { 
      name: 'Wisdom Unlimited', 
      date: '2000',
      impact: "Living from wisdom as an ongoing practice and way of being."
    },
    { 
      name: 'Team Management and Leadership Program (TMLP)', 
      date: '01-Jan-01',
      impact: "Building futures through coordination and integrity; catalyst for World Peace Now."
    },
    { 
      name: 'Wisdom Course On WEB', 
      date: '15-Jun-18',
      impact: "Deepening wisdom practice in the digital age and global community."
    }
  ];

  return (
    <section id="landmark" className="py-24 bg-gradient-to-b from-[#0B1D2A] to-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6 backdrop-blur-sm">
            <GraduationCap className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
              30+ Years of Transformational Education
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <span className="text-[#D4AF37]">Landmark</span> Education Journey
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full mb-6" />
          <p
            className="text-lg text-white/70 max-w-2xl mx-auto"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            A comprehensive journey through transformational education and personal mastery
          </p>
        </div>

        {/* Diploma Highlight */}
        <div className="mb-12">
          <Card className="bg-gradient-to-br from-[#D4AF37]/20 via-[#0B1D2A]/80 to-black border-2 border-[#D4AF37] p-8 text-center backdrop-blur-md shadow-2xl">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <Award className="w-10 h-10 text-black" />
              </div>
              <h3
                className="text-3xl font-bold text-[#D4AF37]"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Diploma of Transformational Mastery
              </h3>
              <p
                className="text-white/90 text-lg max-w-2xl"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                Capstone achievement representing decades of commitment to personal transformation and leadership development
              </p>
              <Badge className="bg-[#D4AF37] text-black text-lg px-6 py-2">
                Master Certification
              </Badge>
            </div>
          </Card>
        </div>

        {/* Courses Timeline */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, index) => (
            <Card
              key={index}
              className="bg-[#0B1D2A]/60 border-[#D4AF37]/20 p-6 hover:border-[#D4AF37] transition-all duration-300 hover:transform hover:scale-105 backdrop-blur-sm group cursor-pointer relative overflow-hidden"
              onClick={() => setActiveCard(activeCard === index ? null : index)}
              onMouseEnter={() => setActiveCard(index)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30 text-xs">
                      {course.date}
                    </Badge>
                  </div>
                  <h4
                    className="text-white font-semibold text-base leading-tight mb-3"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    {course.name}
                  </h4>
                  
                  {/* Impact Description - Revealed on Hover/Click */}
                  <div 
                    className={`transition-all duration-500 ${
                      activeCard === index 
                        ? 'opacity-100 max-h-32 mt-3' 
                        : 'opacity-0 max-h-0 overflow-hidden'
                    }`}
                  >
                    <div className="pt-3 border-t border-[#D4AF37]/20">
                      <p
                        className="text-[#D4AF37]/90 text-sm italic leading-relaxed"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        "{course.impact}"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#D4AF37] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              30+
            </div>
            <div className="text-white/70 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
              Years of Study
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#D4AF37] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              12
            </div>
            <div className="text-white/70 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
              Advanced Programs
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#D4AF37] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              1
            </div>
            <div className="text-white/70 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
              Master Diploma
            </div>
          </div>
        </div>

        {/* The Landmark Discovery - 3 Ontological Moments */}
        <div className="mt-20">
          <Card className="bg-gradient-to-br from-[#D4AF37]/10 via-[#0B1D2A]/80 to-black border-2 border-[#D4AF37]/50 p-8 md:p-12 backdrop-blur-md">
            <h3
              className="text-3xl md:text-4xl font-bold text-[#D4AF37] mb-6 text-center"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              The Landmark Discovery
            </h3>
            
            <p
              className="text-white/80 text-lg mb-8 leading-relaxed text-center italic"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Who I was at aged 30 in 1993 was a function of my childhood. I discovered my 3 ontological moments...
            </p>

            <div className="space-y-8">
              {/* Moment 1 */}
              <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/30 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <span className="text-[#D4AF37] font-bold text-xl" style={{ fontFamily: 'Montserrat, sans-serif' }}>1</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[#D4AF37] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Age 0-5: "Mummy doesn't love me"
                    </h4>
                    <p className="text-white/70 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                      Sibling rivalry story about the cot. The interpretation that shaped early attachment and worth.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Moment 2 */}
              <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/30 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <span className="text-[#D4AF37] font-bold text-xl" style={{ fontFamily: 'Montserrat, sans-serif' }}>2</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[#D4AF37] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Age 8-13: "I don't belong"
                    </h4>
                    <p className="text-white/70 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                      Boarding school story, being late at Victoria Station. Became 'Popular' but kept friends at a distance to avoid the pain of not belonging.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Moment 3 */}
              <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/30 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <span className="text-[#D4AF37] font-bold text-xl" style={{ fontFamily: 'Montserrat, sans-serif' }}>3</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[#D4AF37] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Age 18-22: "I am on my own"
                    </h4>
                    <p className="text-white/70 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                      Amsterdam stabbing story. Became 'Fearless, reckless, BRAVE' to compensate for feeling fundamentally alone and unprotected.
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Conclusion */}
            <div className="mt-8 p-6 bg-black/40 rounded-lg border border-[#D4AF37]/20">
              <p className="text-white/90 text-lg leading-relaxed mb-4" style={{ fontFamily: 'Lato, sans-serif' }}>
                These moments confirmed I was <span className="text-[#D4AF37] font-bold">UNLOVABLE</span>. To cover this, I became <span className="text-[#D4AF37] font-bold">INDEPENDENT</span>, <span className="text-[#D4AF37] font-bold">FUNNY</span>, and <span className="text-[#D4AF37] font-bold">BRAVE</span>.
              </p>
            </div>

            {/* Breakthrough */}
            <div className="mt-6 p-6 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-lg border-2 border-[#D4AF37]/50">
              <p className="text-white text-lg leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                <span className="font-bold text-[#D4AF37]">The Landmark Forum put my past in the past and gave me a BLANK CANVAS to create my life.</span> It allowed me to let my parents' love in and live into a future of Bold Leadership.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LandmarkEducation;
